<template>
    <div class="pb-16 sm:pb-20 lg:pb-28">
        <div class="bg-grey-700 theme-dark">
            <div class="container">
                <div v-if="!latestProgram" class="flex justify-center pt-8 sm:pt-12 mb-5">
                    <ImageBadge
                        :image="model.cover_image"
                        :alt="model.title"
                        :color="model.color"
                        mobile-size="lg"
                        desktop-size="xl"
                    />
                </div>
                <PageHeader
                    :title="model.title"
                    :subtitle="model.subtitle"
                    class="pb-12 sm:pb-16"
                    :style="!latestProgram ? 'padding-top: 0' : ''"
                />
            </div>
        </div>
        <div class="relative">
            <div v-if="latestProgram" class="bg-grey-700 absolute top-0 left-0 w-full h-32 z-0"></div>
            <div class="relative container z-5">
                {{/* * */}}
                {{/* Last played */}}
                {{/* * */}}
                <div
                    v-if="latestProgram"
                    class="bg-black theme-dark text-white py-8 sm:py-14 px-4 sm:px-12 lg:px-16 xl:py-18 xl:px-24 flex flex-col md:flex-row items-center md:items-start"
                >
                    <ImageBadge
                        :image="model.cover_image"
                        :color="model.color"
                        :alt="model.title"
                        mobile-size="lg"
                        desktop-size="xl"
                        class="mb-8 sm:mb-12 md:mb-0"
                    />

                    <div class="md:pl-12 lg:pl-16 xl:pl-24 text-center md:text-left">
                        <div class="flex flex-col sm:flex-row items-center justify-center md:justify-start gap-2 mb-4">
                            <div class="text-xs text-grey-300">Legutóbbi adás</div>
                            <div v-if="latestProgram.start_time && latestProgram.end_time" class="text-xs">
                                {{ $dayjs(latestProgram.date).format('YYYY. MMMM DD, dddd') }} ({{
                                    `${$dayjs(`${latestProgram.date} ${latestProgram.end_time}`).diff(
                                        $dayjs(`${latestProgram.date} ${latestProgram.start_time}`),
                                        'minute'
                                    )} perc`
                                }})
                            </div>
                        </div>

                        <div class="section-title mb-1">{{ latestProgram.title }}</div>

                        <div v-if="latestProgram.content" v-html="$sanitize(latestProgram.content)"></div>

                        <p-button
                            :label="latestProgramPlayButtonLabel"
                            class="mt-6"
                            @click.native="handlePlayButtonClick"
                        >
                            <template #icon-left>
                                <pause-icon
                                    v-if="
                                        $store.state.player.isPlaying &&
                                        $store.state.player.playingProgram?.slug === latestProgram.slug
                                    "
                                />
                                <play-icon v-else />
                            </template>
                        </p-button>
                    </div>
                </div>
                {{/* * */}}
                {{/* Content */}}
                {{/* * */}}
                <div class="grid grid-cols-12 gap-5 pt-12 sm:pt-16">
                    {{/* * */}}
                    {{/* Left side */}}
                    <div class="col-span-12 lg:col-span-8 order-2 lg:order-1">
                        <div v-if="model.content" class="tinymce-content mb-16 lg:mb-24" v-html="model.content"></div>

                        <div v-if="model.related_shows && model.related_shows.length" class="mb-24 lg:mb-28">
                            <h3 class="section-title mb-8">{{ model.title }} rovatok</h3>
                            <div class="grid grid-cols-4 gap-x-5 gap-y-8">
                                <nuxt-link
                                    v-for="(show, i) in model.related_shows"
                                    :key="`related-show-${i}`"
                                    :to="`/musorok/${show.slug}`"
                                    class="col-span-2 sm:col-span-1 flex flex-col items-center"
                                >
                                    <ImageBadge
                                        :image="show.cover_image"
                                        :color="show.color"
                                        :alt="show.title"
                                        desktop-size="md"
                                        mobile-size="sm"
                                    />
                                    <h4 class="font-FibraOne-UltraBold text-center mt-2">{{ show.title }}</h4>
                                </nuxt-link>
                            </div>
                        </div>

                        <div v-if="futurePrograms.length" class="mb-12 lg:mb-16">
                            <div class="border-b border-grey-200 pb-2 mb-5 font-FibraOne-UltraBold">
                                Következő adások
                            </div>
                            <div
                                v-for="(program, i) in futurePrograms.slice(0, displayedPastProgramsCount)"
                                :key="`future-program-${i}`"
                                class="border-b border-grey-200"
                            >
                                <ProgramCard
                                    :to="program.id ? `/adasok/${program.slug}` : null"
                                    :slug="program.slug"
                                    :stream-url="program.stream_url"
                                    :title="program.title"
                                    :color="model.color"
                                    :description="model.title"
                                    :image="model.cover_image"
                                    :prevent-line-clamp="true"
                                    :interval="$dayjs(program.date).format('YYYY. MMMM DD, dddd')"
                                />
                            </div>
                        </div>

                        <div v-if="pastPrograms.length">
                            <div class="border-b border-grey-200 pb-2 mb-5 font-FibraOne-UltraBold">Korábbi adások</div>
                            <div
                                v-for="(program, i) in pastPrograms.slice(0, displayedPastProgramsCount)"
                                :key="`past-program-${i}`"
                                class="border-b border-grey-200"
                            >
                                <ProgramCard
                                    :to="program.id ? `/adasok/${program.slug}` : null"
                                    :slug="program.slug"
                                    :stream-url="program.stream_url"
                                    :title="program.title"
                                    :color="model.color"
                                    :description="model.title"
                                    :image="model.cover_image"
                                    :prevent-line-clamp="true"
                                    :interval="$dayjs(program.date).format('YYYY. MMMM DD, dddd')"
                                />
                            </div>
                            <button
                                v-if="displayedPastProgramsCount < pastPrograms.length"
                                class="text-red-500 hover:underline w-full mt-6"
                                @click="loadPrograms"
                            >
                                Továbbiak
                            </button>
                        </div>
                    </div>
                    {{/* * */}}
                    {{/* Right side */}}
                    <div class="col-span-12 lg:col-span-3 lg:col-start-10 order-1 lg:order-2">
                        <div class="grid grid-cols-2 gap-5 lg:gap-16">
                            <div v-if="schedule.days.length" class="col-span-2 sm:col-span-1 lg:col-span-2">
                                <h3 class="section-title mb-2">Műsoron</h3>

                                <div v-for="day in schedule.days" :key="`schedule-${day.name}`" class="mb-3">
                                    <span>{{ day.label }}</span>
                                    <div v-for="(interval, i) in day.intervals" :key="`interval-${i}`">
                                        {{ interval }}
                                    </div>
                                </div>
                            </div>
                            <div v-if="model.hosts.length" class="col-span-2 sm:col-span-1 lg:col-span-2">
                                <h3 class="section-title mb-4">Műsorvezetők</h3>
                                <nuxt-link
                                    v-for="host in model.hosts"
                                    :key="`host-${host.id}`"
                                    :to="`/musorvezetok/${host.slug}`"
                                    class="flex items-center mb-4"
                                >
                                    <ImageBadge
                                        :image="host.image"
                                        :alt="host.name"
                                        :color="model.color"
                                        desktop-size="sm"
                                        mobile-size="sm"
                                        class="mr-4"
                                    />
                                    <span class="font-FibraOne-UltraBold">{{ host.name }}</span>
                                </nuxt-link>
                            </div>

                            <div
                                v-if="model.related_shows_by_tag.length"
                                class="col-span-2 sm:col-span-1 lg:col-span-2"
                            >
                                <h3 class="section-title mb-4">Hasonló műsorok</h3>
                                <nuxt-link
                                    v-for="show in model.related_shows_by_tag.slice(0, 4)"
                                    :key="`related-show-by-tag-${show.id}`"
                                    :to="`/musorok/${show.slug}`"
                                    class="flex items-center mb-4"
                                >
                                    <ImageBadge
                                        :image="show.cover_image"
                                        :alt="show.title"
                                        :color="show.color"
                                        desktop-size="sm"
                                        mobile-size="sm"
                                        class="mr-4"
                                    />
                                    <span class="font-FibraOne-UltraBold">{{ show.title }}</span>
                                </nuxt-link>
                            </div>
                        </div>
                    </div>

                    <div v-if="bottomBannerData" class="col-span-12 order-3 mb-12 mt-14 lg:mt-20">
                        <BannerCard
                            :title="bottomBannerData.public_title"
                            :label="bottomBannerData.surtitle"
                            :button-text="bottomBannerData.button_text"
                            :slug="bottomBannerData.button_url"
                            :desc="bottomBannerData.content"
                            :image="bottomBannerData.image"
                            :color="bottomBannerData.scheme"
                            :reversed="bottomBannerData.type"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import PageHeader from '../../components/Generic/PageHeader/PageHeader';
import ImageBadge from '../../components/UI/ImageBadge';
import ProgramCard from '../../components/Cards/ProgramCard/ProgramCard';
import BannerCard from '../../components/Cards/BannerCard/BannerCard';

const hungarianDayNames = {
    monday: 'Hétfő',
    tuesday: 'Kedd',
    wednesday: 'Szerda',
    thursday: 'Csütörtök',
    friday: 'Péntek',
    saturday: 'Szombat',
    sunday: 'Vasárnap',
};

export default {
    name: 'Musor',
    components: {
        PageHeader,
        ImageBadge,
        ProgramCard,
        BannerCard,
    },
    async asyncData({ params, $axios, error, $dayjs }) {
        let model = null;
        let latestProgram = null;
        const pastPrograms = [];
        const futurePrograms = [];

        try {
            await $axios
                .$get(`/show/by-slug/${params.musor}`)
                .then((res) => {
                    if (res.success === 1) {
                        model = res.data;

                        res.data.programs.forEach((program) => {
                            if (
                                $dayjs().isSameOrBefore(
                                    $dayjs(`${program.date} ${program.end ? program.end : '00:00'}`),
                                    'minute'
                                )
                            ) {
                                futurePrograms.push(program);
                            } else {
                                if (!latestProgram && program.stream_url) {
                                    latestProgram = program;
                                }
                                pastPrograms.push(program);
                            }
                        });

                        model.programs.shift();
                    }
                })
                .catch((err) => {
                    console.log(err);
                });
        } catch (err) {
            console.log(err);
        }

        if (!model) {
            return error({ statusCode: 404, message: 'Page not found' });
        }

        return { model, latestProgram, pastPrograms, futurePrograms };
    },
    data() {
        return {
            displayedPastProgramsCount: 10,
        };
    },
    head() {
        const meta = [
            {
                hid: 'og:title',
                property: 'og:title',
                content: this.model.title,
            },
            {
                hid: 'title',
                name: 'title',
                content: this.model.title,
            },
            {
                hid: 'og:description',
                property: 'og:description',
                content: this.model.subtitle,
            },
            {
                hid: 'description',
                name: 'description',
                content: this.model.subtitle,
            },
        ];

        return {
            title: this.model.title,
            meta,
        };
    },
    computed: {
        schedule() {
            const _rawDaysData = this.model.schedule.reduce((acc, schedule) => {
                Object.keys(schedule).forEach((key) => {
                    if (key.match(/_start$/) && schedule[key]) {
                        const dayName = key.replace('_start', '');
                        const startTime = `${schedule[`${dayName}_start`]}`;
                        const endTime = `${schedule[`${dayName}_end`]}`;
                        const interval =
                            startTime && endTime ? `${startTime.slice(0, 5)}-${endTime.slice(0, 5)}` : null;

                        if (!interval) {
                            return acc;
                        }

                        if (!acc.find((day) => day.name === dayName)) {
                            acc.push({
                                name: dayName,
                                label: hungarianDayNames[dayName],
                                intervals: [interval],
                            });
                        } else {
                            acc.find((x) => x.name === dayName).intervals.push(interval);
                        }
                    }
                });

                return acc;
            }, []);

            const result = _rawDaysData.reduce(
                (acc, curr, i, arr) => {
                    const mondayData = arr.find((x) => x.name === 'monday');
                    acc.days.push(curr);
                    if (mondayData && JSON.stringify(curr.intervals) === JSON.stringify(mondayData.intervals)) {
                        acc.same_days_count++;
                        acc[`same_on_monday_and_${curr.name}`] = true;
                    }

                    return acc;
                },
                { days: [], same_days_count: 0 }
            );

            if (!result.days.length) {
                return result;
            }

            if (result.same_days_count === 7) {
                result.days.splice(0, 7, {
                    label: 'Minden nap',
                    intervals: result.days[0].intervals,
                });
            } else if (
                (result.same_days_count === 5 &&
                    !result.same_on_monday_and_saturday &&
                    !result.same_on_monday_and_sunday) ||
                (result.same_days_count === 6 &&
                    (result.same_on_monday_and_saturday || result.same_on_monday_and_sunday))
            ) {
                result.days.splice(0, 5, {
                    label: 'Hétköznaponként',
                    intervals: result.days[0].intervals,
                });
            }

            return result;
        },
        latestProgramPlayButtonLabel() {
            if (
                this.$store.state.player.playingProgram?.slug === this.latestProgram.slug &&
                this.$store.state.player.isPlaying
            ) {
                return 'Szünet';
            }
            return 'Lejátszás';
        },
        bottomBannerData() {
            return this.getPromotionBox('show-bottom');
        },
    },
    methods: {
        loadPrograms() {
            this.displayedPastProgramsCount += 10;
        },
        handlePlayButtonClick() {
            // If the program's data is already loaded into the player, just toggle the player
            if (this.$store.state.player.playingProgram?.slug === this.latestProgram.slug) {
                this.$nuxt.$emit('toggleMainPlayerPlayState');
                return;
            }

            // Else we update the player with the selected program.
            // This will result in redundant updates too, but it doesn't do any harm, so it is what it is
            const payload = {};

            payload.src = this.latestProgram.stream_url;
            payload.color = this.model.color;
            payload.artist = this.model.title;
            payload.slug = this.latestProgram.slug;
            payload.title = this.latestProgram.title;
            payload.image = this.model.cover_image;

            this.$store.commit('player/setPlayingChannelName', null);
            this.$store.commit('player/setPlayingProgram', payload);
            this.$nuxt.$emit('updateMainPlayerStreamUrl', payload.src);
            this.$nuxt.$emit('playMainPlayer');
        },
    },
};
</script>

<style scoped></style>
